import { useEffect, useMemo, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { statusBadgeClass, fmtDate, inr, formatApiError } from "@/lib/utils";
import { Plus, Search, Download, Upload, Pencil, Trash2, X, Save, Link2, MessageCircle, Images, Archive, ArchiveRestore } from "lucide-react";
import BackupDriveBadge from "@/components/BackupDriveBadge";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import ImportMapper from "@/components/ImportMapper";
import PageHero from "@/components/PageHero";
import MetricCard from "@/components/MetricCard";
import { IndianRupee } from "lucide-react";

const emptyForm = {
  event_date: "",
  couple_name: "",
  event: "Wedding",
  status: "All Done",
  coordinator: "",
  contact: "",
  booking_id: "",
  total_amount: 0,
  paid_amount: 0,
  notes: "",
};

const STATUS_OPTIONS = [
  "All Done",
  "Correction",
  "Delivery Pending",
  "Payment Pending",
  "Delivery Pending, Payment Pending",
  "Data Ready, Delivery Pending",
  "Reel Pending",
  "Work Done",
  "Working",
  "Delivery Done, Payment Pending",
  "Shoot Not Happen",
];

const EVENT_OPTIONS = ["Wedding", "Engagement", "Pre-Wedding", "Birthday", "Naming Ceremony", "Other"];

// iter37 — Micro-badge summarising deliverable tasks per row.
const TASK_STATUS_STYLES = {
  pending:     "bg-stone-100 text-stone-600 border-stone-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  done:        "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const TASK_STATUS_LABELS = {
  pending:     "Pending",
  in_progress: "In-Progress",
  done:        "Done",
};

function DeliverableBadges({ tasks }) {
  if (!tasks || tasks.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1" data-testid="pp-deliverable-badges">
      {tasks.map((t) => {
        const asg = t.assigned_staff_name || "";
        const label = TASK_STATUS_LABELS[t.status] || t.status;
        const style = TASK_STATUS_STYLES[t.status] || TASK_STATUS_STYLES.pending;
        const suffix = t.status === "pending" && !asg
          ? "Unassigned"
          : asg
            ? `${label} (${asg.split(" ")[0]})`
            : label;
        return (
          <span
            key={t.id}
            className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${style}`}
            title={`${t.label} — ${t.source}${asg ? ` · ${asg}` : ""}`}
            data-testid={`pp-badge-${t.id}`}
          >
            <b>{t.label}:</b> {suffix}
          </span>
        );
      })}
    </div>
  );
}


export default function PostProduction() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tasksByCouple, setTasksByCouple] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [coordFilter, setCoordFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const [showImport, setShowImport] = useState(false);
  // iter53 — Active vs Client History toggle. Backend reads `archived` query
  // param; UI reflects the state via testid `pp-view-toggle-*`.
  const [view, setView] = useState("active");   // "active" | "history"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, tasksResp] = await Promise.all([
        api.get(`/postproduction?archived=${view === "history"}`),
        api.get("/post-production/tasks").catch(() => ({ data: [] })),
      ]);
      setItems(data);
      // iter37 — group deliverable tasks by couple_name (case-insensitive) so
      // the STATUS column can render mini-badges without an extra fetch/row.
      const byCouple = new Map();
      for (const t of tasksResp.data || []) {
        const k = (t.couple_name || "").trim().toLowerCase();
        if (!k) continue;
        if (!byCouple.has(k)) byCouple.set(k, []);
        byCouple.get(k).push(t);
      }
      setTasksByCouple(byCouple);
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { load(); }, [load]);

  // iter120 — Financial Blindness for coordinator + manager + viewer.
  // Only admin sees financial columns, edit inputs, and revenue totals.
  const isCoord = user?.role !== "admin";

  const coordinators = useMemo(() => Array.from(new Set(items.map((i) => i.coordinator).filter(Boolean))), [items]);
  const statuses = useMemo(() => Array.from(new Set(items.map((i) => i.status).filter(Boolean))), [items]);

  const filtered = useMemo(() => {
    return items.filter((b) => {
      if (coordFilter !== "ALL" && b.coordinator !== coordFilter) return false;
      if (statusFilter !== "ALL" && b.status !== statusFilter) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      // iter107 — Include hard-drive labels in the search haystack.
      return [
        b.couple_name, b.event, b.coordinator, b.notes,
        b.backup_drive_1, b.backup_drive_2, b.backup_drive_3, b.backup_drive_4_drone,
      ].filter(Boolean).some((x) => String(x).toLowerCase().includes(s));
    });
  }, [items, q, coordFilter, statusFilter]);

  const totals = useMemo(() => {
    // Prefer card-synced values (iter28): TOTAL = card total, PAID = Σ received
    // milestones. Falls back to legacy total_amount / paid_amount fields for
    // rows that don't match any Shoot Crew group.
    const t = filtered.reduce((acc, x) => {
      const tot = x.is_card_synced ? Number(x.card_total || 0) : Number(x.total_amount || 0);
      const pd = x.is_card_synced ? Number(x.card_paid || 0) : Number(x.paid_amount || 0);
      acc.total += tot;
      acc.paid += pd;
      return acc;
    }, { total: 0, paid: 0 });
    t.pending = t.total - t.paid;
    return t;
  }, [filtered]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setErr(""); setShowForm(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...emptyForm, ...b }); setErr(""); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      // iter52 — Backend auto-sets manual_override=True on every PUT so the
      // studio owner's typed values become the source of truth. The frontend
      // only ever needs to explicitly send `manual_override=false` when the
      // user clicks "Revert to auto-sync" (which also blanks the amounts).
      const isRevert = editing && form.manual_override === false && Number(form.total_amount || 0) === 0 && Number(form.paid_amount || 0) === 0;
      const payload = {
        ...form,
        total_amount: Number(form.total_amount || 0),
        paid_amount: Number(form.paid_amount || 0),
        manual_override: !isRevert,
      };
      if (editing) await api.put(`/postproduction/${editing.id}`, payload);
      else await api.post("/postproduction", payload);
      setShowForm(false);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/postproduction/${id}`);
      load();
    } catch (e) {      alert(formatApiError(e.response?.data?.detail));
    }
  };

  // iter53 — soft-archive: never permanently delete, moves to Client History.
  const archive = async (id) => {
    try {
      await api.post(`/postproduction/${id}/archive`);
      load();
    } catch (e) { alert(formatApiError(e.response?.data?.detail)); }
  };
  const restore = async (id) => {
    try {
      await api.post(`/postproduction/${id}/restore`);
      load();
    } catch (e) { alert(formatApiError(e.response?.data?.detail)); }
  };

  const exportCsv = () => window.open(`${API}/export/postproduction`, "_blank");

  const pending = (b) => {
    if (b.is_card_synced) return Number(b.card_pending || 0);
    return Number(b.total_amount || 0) - Number(b.paid_amount || 0);
  };
  const rowTotal = (b) => (b.is_card_synced ? Number(b.card_total || 0) : Number(b.total_amount || 0));
  const rowPaid  = (b) => (b.is_card_synced ? Number(b.card_paid  || 0) : Number(b.paid_amount  || 0));

  return (
    <div className="space-y-6 fade-in" data-testid="postproduction-page">
      <PageHero
        eyebrow="Kahani Studio · Post-Production"
        title="Post-Production Pipeline"
        subtitle="Delivery, corrections, and payment milestones — every couple's story tracked end-to-end."
        right={
          <>
            <Link to="/gallery" className="btn btn-outline" data-testid="pp-open-gallery-btn">
              <Images className="w-4 h-4" strokeWidth={1.6} /> AI Gallery
            </Link>
            <button className="btn btn-outline" onClick={exportCsv} data-testid="export-pp-btn">
              <Download className="w-4 h-4" strokeWidth={1.6} /> Export
            </button>
            {user?.role === "admin" && (
              <button className="btn btn-outline" onClick={() => setShowImport(true)} data-testid="import-pp-btn">
                <Upload className="w-4 h-4" strokeWidth={1.6} /> Import
              </button>
            )}
            <button className="btn btn-primary" onClick={openCreate} data-testid="new-pp-btn">
              <Plus className="w-4 h-4" strokeWidth={1.6} /> New entry
            </button>
          </>
        }
      />

      {/* Summary row — hidden for coordinator (financial blindness).
          iter56 — upgraded from dense .card p-3 tiles to editorial MetricCards. */}
      {!isCoord && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="pp-summary-row">
          <MetricCard
            eyebrow="Total Billed"
            value={inr(totals.total)}
            subtitle="Across all active projects"
            icon={IndianRupee}
            tone="neutral"
          />
          <MetricCard
            eyebrow="Paid"
            value={inr(totals.paid)}
            subtitle="Received in the ledger"
            icon={IndianRupee}
            tone="emerald"
          />
          <MetricCard
            eyebrow="Pending"
            value={inr(totals.pending)}
            subtitle="Outstanding across all events"
            icon={IndianRupee}
            tone={totals.pending > 0 ? "red" : "neutral"}
          />
        </div>
      )}

      {/* iter53 — Active / Client History view toggle */}
      <div className="flex items-center gap-1 p-1 rounded-lg border border-stone-200 bg-stone-50 w-fit" role="tablist" data-testid="pp-view-toggle">
        <button
          type="button"
          role="tab"
          aria-selected={view === "active"}
          onClick={() => setView("active")}
          className={`px-4 py-1.5 rounded-md text-xs font-semibold transition ${
            view === "active"
              ? "bg-white text-stone-900 shadow-sm border border-stone-200"
              : "text-stone-500 hover:text-stone-800"
          }`}
          data-testid="pp-view-toggle-active"
        >
          Active Clients
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "history"}
          onClick={() => setView("history")}
          className={`px-4 py-1.5 rounded-md text-xs font-semibold transition inline-flex items-center gap-1.5 ${
            view === "history"
              ? "bg-white text-stone-900 shadow-sm border border-stone-200"
              : "text-stone-500 hover:text-stone-800"
          }`}
          data-testid="pp-view-toggle-history"
        >
          <Archive className="w-3.5 h-3.5" strokeWidth={2} />
          Client History
        </button>
      </div>

      {/* Filters */}
      <div className="card p-3 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" strokeWidth={1.5} />
          <input
            className="input pl-9"
            placeholder="Search couple, coordinator, notes…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="pp-search"
          />
        </div>
        <select className="input w-44" value={coordFilter} onChange={(e) => setCoordFilter(e.target.value)} data-testid="pp-coord-filter">
          <option value="ALL">All coordinators</option>
          {coordinators.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="input w-52" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="pp-status-filter">
          <option value="ALL">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th>Date</th>
                <th>Couple</th>
                <th>Event</th>
                <th>Status</th>
                <th>Coordinator</th>
                {!isCoord && <th className="text-right" data-testid="pp-col-total">Total</th>}
                {!isCoord && <th className="text-right" data-testid="pp-col-paid">Paid</th>}
                {!isCoord && <th className="text-right" data-testid="pp-col-pending">Pending</th>}
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isCoord ? 6 : 9} className="text-center text-stone-500 py-10">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={isCoord ? 6 : 9} className="text-center text-stone-500 py-10">No entries found</td></tr>
              ) : (
                filtered.map((b) => {
                  const pend = pending(b);
                  return (
                    <tr key={b.id} data-testid={`pp-row-${b.id}`}>
                      <td className="font-mono text-xs">{fmtDate(b.event_date)}</td>
                      <td className="font-medium">
                        <div className="flex items-center gap-1.5">
                          {b.couple_name}
                          {b.booking_id && (
                            <span title="Linked to a booking" className="inline-flex" data-testid={`pp-linked-${b.id}`}>
                              <Link2 className="w-3.5 h-3.5 text-rose-700" strokeWidth={1.8} />
                            </span>
                          )}
                        </div>
                        {/* iter107 — Hard-drive badge under the couple name.
                             Auto-hides when no drive is set. */}
                        <BackupDriveBadge item={b} size="xs" className="mt-1" testid={`pp-drive-badge-${b.id}`} />
                      </td>
                      <td>{b.event}</td>
                      <td>
                        <div className="flex flex-col gap-1" data-testid={`pp-status-cell-${b.id}`}>
                          <span className={statusBadgeClass(b.status)}>{b.status}</span>
                          <DeliverableBadges tasks={tasksByCouple.get((b.couple_name || "").trim().toLowerCase()) || []} />
                        </div>
                      </td>
                      <td>{b.coordinator || "—"}</td>
                      {!isCoord && (
                        <td className="text-right font-mono tabular-nums" data-testid={`pp-total-${b.id}`}>
                          {inr(rowTotal(b))}
                          {b.is_card_synced && (
                            <span title="Auto-synced from client card" className="ml-1 text-[9px] uppercase tracking-wider text-rose-700 font-semibold" data-testid={`pp-synced-${b.id}`}>
                              · card
                            </span>
                          )}
                        </td>
                      )}
                      {!isCoord && (
                        <td className="text-right font-mono tabular-nums text-green-700" data-testid={`pp-paid-${b.id}`}>{inr(rowPaid(b))}</td>
                      )}
                      {!isCoord && (
                        <td className={`text-right font-mono tabular-nums ${pend > 0 ? "text-red-700 font-semibold" : "text-stone-500"}`} data-testid={`pp-pending-${b.id}`}>{pend > 0 ? inr(pend) : "all Paid"}</td>
                      )}
                      <td className="text-right whitespace-nowrap">
                        <button onClick={() => openEdit(b)} className="inline-flex items-center justify-center w-7 h-7 rounded-md text-stone-700 hover:bg-stone-100" data-testid={`pp-edit-${b.id}`}>
                          <Pencil className="w-4 h-4" strokeWidth={1.6} />
                        </button>
                        {user?.role === "admin" && view === "active" && (
                          <button
                            onClick={() => archive(b.id)}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-amber-800 hover:bg-amber-50"
                            title="Move to Client History"
                            data-testid={`pp-archive-${b.id}`}
                          >
                            <Archive className="w-4 h-4" strokeWidth={1.6} />
                          </button>
                        )}
                        {user?.role === "admin" && view === "history" && (
                          <button
                            onClick={() => restore(b.id)}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-emerald-700 hover:bg-emerald-50"
                            title="Restore to Active"
                            data-testid={`pp-restore-${b.id}`}
                          >
                            <ArchiveRestore className="w-4 h-4" strokeWidth={1.6} />
                          </button>
                        )}
                        {user?.role === "admin" && (
                          <button onClick={() => del(b.id)} className="inline-flex items-center justify-center w-7 h-7 rounded-md text-red-700 hover:bg-red-50" data-testid={`pp-delete-${b.id}`}>
                            <Trash2 className="w-4 h-4" strokeWidth={1.6} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/30 fade-in" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 h-16 border-b border-stone-200">
              <h2 className="font-heading text-lg font-semibold">{editing ? "Edit entry" : "New entry"}</h2>
              <button className="btn-ghost p-2 rounded" onClick={() => setShowForm(false)} data-testid="pp-form-close">
                <X className="w-5 h-5" strokeWidth={1.6} />
              </button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Event date *</label>
                  <input type="date" required className="input" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="pp-form-date" />
                </div>
                <div>
                  <label className="label">Event</label>
                  <select className="input" value={form.event} onChange={(e) => setForm({ ...form, event: e.target.value })} data-testid="pp-form-event">
                    {EVENT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="label">Couple name *</label>
                <input required className="input" value={form.couple_name} onChange={(e) => setForm({ ...form, couple_name: e.target.value })} data-testid="pp-form-couple" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Status</label>
                  <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="pp-form-status">
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Coordinator</label>
                  <input className="input" value={form.coordinator} onChange={(e) => setForm({ ...form, coordinator: e.target.value })} placeholder="Preeti, Rakesh…" data-testid="pp-form-coord" />
                </div>
              </div>
              <div>
                <label className="label">Client contact (for WhatsApp reminders)</label>
                <input
                  className="input"
                  value={form.contact}
                  onChange={(e) => setForm({ ...form, contact: e.target.value })}
                  placeholder="10-digit number (auto-filled if linked from a booking)"
                  data-testid="pp-form-contact"
                />
              </div>
              {!isCoord && (
                <div className="space-y-2">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="label">Total (₹)</label>
                      <input type="number" className="input" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} data-testid="pp-form-total" />
                    </div>
                    <div>
                      <label className="label">Paid (₹)</label>
                      {editing?.paid_from_ledger ? (
                        <>
                          <input
                            disabled
                            className="input bg-emerald-50 font-mono border-emerald-200 text-emerald-800"
                            value={inr(Number(form.paid_amount || 0))}
                            data-testid="pp-form-paid"
                          />
                          <div className="mt-1 text-[10px] tracking-wider uppercase text-emerald-700 font-semibold" data-testid="pp-form-paid-ledger-notice">
                            Read-only · From Financial Center ledger
                          </div>
                        </>
                      ) : (
                        <input type="number" className="input" value={form.paid_amount} onChange={(e) => setForm({ ...form, paid_amount: e.target.value })} data-testid="pp-form-paid" />
                      )}
                    </div>
                    <div>
                      <label className="label">Pending (auto)</label>
                      <input disabled className="input bg-stone-50 font-mono" value={inr(Number(form.total_amount || 0) - Number(form.paid_amount || 0))} data-testid="pp-form-pending" />
                    </div>
                  </div>
                  {/* iter52 — Manual override notice + revert affordance.
                      When the user has explicitly saved amounts here they take
                      precedence over the auto-sync from shoot_crew_groups +
                      received milestones. Clicking "Revert to auto-sync" clears
                      the override flag AND blanks the manual amounts, so the
                      matrix + invoice re-pull from card totals. */}
                  {editing?.manual_override ? (
                    <div className="flex items-start justify-between gap-3 text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="pp-manual-override-notice">
                      <div className="text-amber-900">
                        <div className="font-semibold">Manual amounts active</div>
                        <div className="text-amber-800/80">
                          The values you save here override the auto-sync from Shoot Crew Management &amp; payment milestones — the matrix, pending calc, and invoice PDF will all use these figures.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, manual_override: false, total_amount: 0, paid_amount: 0 }))}
                        className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-amber-900 hover:text-amber-950 underline"
                        data-testid="pp-manual-override-revert"
                      >
                        Revert to auto-sync
                      </button>
                    </div>
                  ) : (
                    <div className="text-[11px] text-stone-500 italic px-1" data-testid="pp-manual-override-hint">
                      Tip: leave blank to auto-sync from Shoot Crew total + received milestones. Any value you type &amp; save here will override the auto-sync.
                    </div>
                  )}
                </div>
              )}
              <div>
                <label className="label">Notes</label>
                <textarea className="input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="pp-form-notes" />
              </div>

              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="pp-form-save">
                  <Save className="w-4 h-4" strokeWidth={1.6} /> {editing ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showImport && (
        <ImportMapper
          kind="postproduction"
          open={showImport}
          onClose={() => setShowImport(false)}
          onDone={load}
        />
      )}
    </div>
  );
}
